import { queryOptions, UseQueryOptions } from "@tanstack/react-query";
import fetcher from "~/components/build/Show/lib/fetcher";
import { Build, JobStates } from "~/components/build/Show/lib/types";
import { Step } from "~/lib/pipeline";

export type WaterfallBarType = {
  limited: {
    start_percentage: number;
    percentage: number;
    duration: string;
  };
  active: {
    start_percentage: number;
    waiting_percentage: number;
    waiting_duration: string;
    dispatching_percentage: number;
    dispatching_duration: string;
    running_percentage: number;
    running_duration: string;
    total_percentage: number;
    total_duration: string;
  };
  passed: boolean;
  step_uuid?: string;
  job_uuid?: string;
  job_url?: string;
  parent: boolean;
};

export type WaterfallRowType = {
  bars: WaterfallBarType[];
  children: WaterfallRowType[];
  end_time: null | string;
  type: Step["type"];
  job_uuid?: string;
  step_uuid: string;
  job_url?: string | null;
  job_state?: JobStates;
  job_passed?: boolean;
  job_soft_failed?: boolean;
  label: { text: string; format: "emojify" | "code" | "raw" };
  start_time: null | string;
  duration: string;
} & ({ parallel_group_index: number; parallel_group_total: number } | undefined);

export type WaterfallData = {
  chart_data: WaterfallRowType[];
  bar_container_padding: number;
};

export const waterfallQueryKey = (buildId: Build["id"]) => {
  return ["build", buildId, "waterfall"];
};

/**
 * Above this many jobs the waterfall request takes long enough to time out for
 * many users (~30s spinner). Skip the fetch and surface a clear empty state
 * instead so people aren't left staring at a loading state before bouncing.
 *
 * We gate on jobs (not steps) because the waterfall renders a row per job —
 * parallel groups and retries multiply step counts into many more jobs, and
 * job count is what actually drives request and render time.
 */
export const WATERFALL_MAX_JOBS = 5000;

export const isWaterfallTooLarge = (build: Build): boolean => {
  const jobsCount = build.statistics?.jobsCount;
  if (jobsCount === undefined) {
    return false;
  }
  return jobsCount > WATERFALL_MAX_JOBS;
};

export const waterfallQueryOptions = (
  build: Build,
  queryOptionsArgs: Omit<UseQueryOptions<WaterfallData>, "queryKey" | "queryFn"> = {},
) => {
  const { enabled: callerEnabled, ...restOptions } = queryOptionsArgs;
  const enabled =
    !Features.DisableStepsWaterfallView && !isWaterfallTooLarge(build) && (callerEnabled ?? true);

  return queryOptions({
    queryKey: waterfallQueryKey(build.id),
    queryFn: ({ signal }) => fetchWaterfallData(build.path, signal),
    enabled,
    ...restOptions,
  });
};

function fetchWaterfallData(buildPath: Build["path"], signal: AbortSignal): Promise<WaterfallData> {
  const url = new URL(`${buildPath}/waterfall_tab.json`, window.location.origin);
  return fetcher<WaterfallData>(url, false, { signal });
}
